import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../../redux/Store';
import { getCodeFromUrl, isUserAuthenticated, redirectToAuthPage } from '../../utils/TokenManager';
import { fetchTokenUsingAuthCode } from '../../redux/Auth/AuthSlice';
import LoaderPage from '../../pages/Auth/LoaderPage';
import { PATHS } from '../../routes/Routes';
import { getUserDetails } from '../../redux/User/UserSlice';

/**
 * AutoLogin Component
 *
 * Handles the SSO authentication flow automatically by:
 * 1. Checking for existing authentication
 * 2. Processing auth codes from redirect URLs
 * 3. Managing token fetch operations
 * 4. Redirecting users based on authentication status
 *
 * This component acts as a middleware between the authentication service
 * and protected application routes.
 */
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { accessTokenStatus } = useSelector((state: RootState) => state.auth);
  const isAuthenticated = isUserAuthenticated();
  const authCode = getCodeFromUrl();

  /**
   * Primary effect for handling the authentication flow
   * Runs when auth code, authentication status
   */
  useEffect(() => {
    if (!isAuthenticated) {
      if (authCode) {
        dispatch(fetchTokenUsingAuthCode(authCode));
      } else {
        redirectToAuthPage();
      }
    } else {
      dispatch(getUserDetails());
    }
  }, [authCode]);

  /**
   * Secondary effect for handling token status changes
   * Redirects user based on the success/failure of token acquisition
   */
  useEffect(() => {
    if (accessTokenStatus === 'failed') {
      redirectToAuthPage();
    }
    if (accessTokenStatus === 'succeeded') {
      const preserveUrl = sessionStorage.getItem('preserveUrl');
       navigate(preserveUrl ? preserveUrl : PATHS.LANDING);
    }
  }, [accessTokenStatus]);


  // Show loading indicator while token acquisition is in progress
  if (accessTokenStatus === 'loading') {
    return <LoaderPage />;
  }

  if (isUserAuthenticated()) {
    return <>{children}</>;
  }

  // Return null when the component is handling redirections
  return null;
};

export default AuthProvider;
